// AB3 Academy — клиент learning_service API.
// Все запросы через authService.fetchWithAuth (включая sandbox header injection).
// Контракт: handoff Phase 2 §3.

import authService from './auth.service';
import type {
  AllowedTablesResponse,
  CourseDetailOut,
  CourseListParams,
  CourseListResponse,
  CourseOut,
  CoursePayload,
  CourseReorderItem,
  CourseReorderResponse,
  CourseUpdatePayload,
  EnrollmentOut,
  EnrollmentStatusResponse,
  FinishModuleResponse,
  FinishPracticeResponse,
  ImportRequest,
  ImportResult,
  ModuleListResponse,
  ModuleOut,
  ModulePayload,
  ModuleType,
  ModuleUpdatePayload,
  PracticeHeaderResponse,
  PresentationImportCommitRequest,
  PresentationImportResult,
  PresentationPageResponse,
  PresentationUrlResponse,
  ReversionResult,
  StartModuleResponse,
  StartPracticeResponse,
  ValidateSeedSpecRequest,
  ValidateSeedSpecResponse,
} from '../types/learning.types';

const API_URL = '/api/v1';

class LearningService {
  // ─── helpers ──────────────────────────────────────────────────────────────

  private buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return query ? `?${query}` : '';
  }

  private async asJson<T>(res: Response, fallbackMessage: string): Promise<T> {
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const detail = (err?.detail as string) || fallbackMessage;
      // Прикрепляем status + detail к ошибке, чтобы presentationErrors
      // (getErrorStatus / mapPresentationErrorFromException) мог корректно
      // разрулить 422/409/503 по статусу, а не падать в generic-500.
      // .message остаётся = detail (совместимо с потребителями по message).
      const error = new Error(detail) as Error & { status?: number; detail?: string | null };
      error.status = res.status;
      error.detail = (err?.detail as string) ?? null;
      throw error;
    }
    return res.json() as Promise<T>;
  }

  // ─── COURSES (admin CRUD + learner read) ─────────────────────────────────

  async listCourses(params: CourseListParams = {}): Promise<CourseListResponse> {
    const query = this.buildQuery({
      offset: params.offset,
      limit: params.limit,
      include_drafts: params.include_drafts,
    });
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses${query}`);
    const data = await this.asJson<CourseListResponse & { org_members_count?: number }>(
      res,
      'Не удалось загрузить курсы',
    );
    // Бэкенд отдаёт org_members_count; старый контракт — org_users_count. Берём любой.
    const members = data.org_members_count ?? data.org_users_count ?? 0;
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number(data.total ?? 0),
      org_users_count: Number(members),
    };
  }

  async getCourse(courseId: number): Promise<CourseOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses/${courseId}`);
    return this.asJson<CourseOut>(res, 'Курс не найден');
  }

  /**
   * Academy-003/004: курс + все модули одним запросом. Бэкенд для read-only
   * view-страницы /admin/academy/courses/:id/view. Cross-org / draft для
   * learner → 404 (бэкенд скрывает существование).
   */
  async getCourseFull(courseId: number): Promise<CourseDetailOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses/${courseId}/full`);
    const data = await this.asJson<CourseDetailOut>(res, 'Курс не найден');
    return { ...data, modules: Array.isArray(data.modules) ? data.modules : [] };
  }

  async createCourse(payload: CoursePayload): Promise<CourseOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.asJson<CourseOut>(res, 'Не удалось создать курс');
  }

  async updateCourse(courseId: number, payload: CourseUpdatePayload): Promise<CourseOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses/${courseId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return this.asJson<CourseOut>(res, 'Не удалось обновить курс');
  }

  /**
   * Порядок курсов в каталоге (admin drag-and-drop).
   * updated_count < items.length означает, что часть id пропущена (курс удалён
   * или принадлежит другой организации) — список устарел, caller обязан
   * перезапросить listCourses.
   */
  async reorderCourses(items: CourseReorderItem[]): Promise<CourseReorderResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    });
    return this.asJson<CourseReorderResponse>(res, 'Не удалось изменить порядок курсов');
  }

  async deleteCourse(courseId: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses/${courseId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить курс');
    }
  }

  /**
   * Дублирование курса. Backend clone-эндпоинта нет — собираем на фронте:
   * getCourseFull → createCourse (всегда черновик) → createModule по каждому модулю.
   * Частичный сбой не откатывается: курс уже создан, возвращаем сколько модулей
   * успели скопировать (caller покажет toast и откроет новый курс).
   */
  async duplicateCourse(courseId: number): Promise<{ course: CourseOut; copiedModules: number; totalModules: number }> {
    const full = await this.getCourseFull(courseId);
    const created = await this.createCourse({
      title: `${full.title ?? 'Курс'} (копия)`,
      description: full.description ?? null,
      is_published: false,
    });
    const modules = [...(full.modules ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    let copied = 0;
    for (const m of modules) {
      try {
        await this.createModule(created.id, {
          order: m.order ?? copied,
          title: m.title ?? 'Модуль',
          type: (m.type as ModuleType | null) ?? 'text',
          content_storage_key: m.content_storage_key ?? null,
          content_markdown: m.content_markdown ?? null,
          quiz_test_template_id: m.quiz_test_template_id ?? null,
          practice_seed_spec: m.practice_seed_spec ?? null,
          practice_expected_state: m.practice_expected_state ?? null,
          is_required: m.is_required ?? true,
        });
        copied += 1;
      } catch {
        // битый модуль не валит остальную копию — пропускаем и продолжаем
        continue;
      }
    }
    return { course: created, copiedModules: copied, totalModules: modules.length };
  }

  // ─── MODULES (admin CRUD + learner read) ─────────────────────────────────

  async listModules(courseId: number, opts: { offset?: number; limit?: number } = {}): Promise<ModuleListResponse> {
    const query = this.buildQuery({ offset: opts.offset, limit: opts.limit });
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/courses/${courseId}/modules${query}`,
    );
    const data = await this.asJson<ModuleListResponse>(res, 'Не удалось загрузить модули');
    const items = Array.isArray(data.items) ? data.items : [];
    // Backend контракт не гарантирует сортировку по order — handoff §3.2.
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return { items, total: Number(data.total ?? items.length) };
  }

  async getModule(courseId: number, moduleId: number): Promise<ModuleOut> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/courses/${courseId}/modules/${moduleId}`,
    );
    return this.asJson<ModuleOut>(res, 'Модуль не найден');
  }

  async createModule(courseId: number, payload: ModulePayload): Promise<ModuleOut> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/courses/${courseId}/modules`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
    return this.asJson<ModuleOut>(res, 'Не удалось создать модуль');
  }

  async updateModule(courseId: number, moduleId: number, payload: ModuleUpdatePayload): Promise<ModuleOut> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/courses/${courseId}/modules/${moduleId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    );
    return this.asJson<ModuleOut>(res, 'Не удалось обновить модуль');
  }

  async deleteModule(courseId: number, moduleId: number): Promise<void> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/courses/${courseId}/modules/${moduleId}`,
      { method: 'DELETE' },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить модуль');
    }
  }

  // ─── PRACTICE AUTHORING (Academy-001/006) ────────────────────────────────

  // TTL cache для allowed-tables: бэкенд не кэширует, фронт держит 5 минут.
  // Admin открывает ModuleEditor несколько раз подряд — не дёргаем рефлексию
  // каждый раз. Инвалидируется по time, не по событию (handoff §2.4).
  private allowedTablesCache: { value: AllowedTablesResponse; at: number } | null = null;
  private readonly ALLOWED_TABLES_TTL_MS = 5 * 60 * 1000;

  /**
   * Academy-006. Whitelist таблиц+колонок для practice seed_spec.
   * Permission: learning:practice:author. Sensitive колонки уже отфильтрованы
   * на бэке — фронту не нужен hardcoded blacklist.
   * Cache 5min: handoff §2.4 запрещает per-keystroke вызовы.
   */
  async getAllowedTables(force = false): Promise<AllowedTablesResponse> {
    const now = Date.now();
    if (!force && this.allowedTablesCache && now - this.allowedTablesCache.at < this.ALLOWED_TABLES_TTL_MS) {
      return this.allowedTablesCache.value;
    }
    const res = await authService.fetchWithAuth(`${API_URL}/learning/practice/allowed-tables`);
    const data = await this.asJson<AllowedTablesResponse>(res, 'Не удалось загрузить список таблиц');
    const normalized: AllowedTablesResponse = {
      tables: Array.isArray(data.tables) ? data.tables : [],
    };
    this.allowedTablesCache = { value: normalized, at: now };
    return normalized;
  }

  /**
   * Academy-001. Dry-run валидация practice seed_spec + expected_state.
   * ⚠️ Всегда HTTP 200, ошибки в body.errors[] (НЕ трактовать success: false
   * как HTTP-ошибку). Не создаёт схему, не пишет в БД.
   */
  async validatePracticeSpec(payload: ValidateSeedSpecRequest): Promise<ValidateSeedSpecResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/practice/validate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await this.asJson<ValidateSeedSpecResponse>(res, 'Не удалось проверить practice');
    return {
      success: Boolean(data.success),
      errors: Array.isArray(data.errors) ? data.errors : [],
    };
  }

  // ─── COURSE IMPORT (Academy-002) ─────────────────────────────────────────

  /**
   * Two-phase import: dry_run → commit с preview_token TOCTOU-защитой.
   * Если payload между preview и commit отличается → 409, нужно re-trigger
   * dry_run (handoff §3.2). Per-курс SAVEPOINT на бэке — битый курс не валит
   * остальных, валидные создаются.
   */
  async importCourses(payload: ImportRequest): Promise<ImportResult> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/courses/import`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const data = await this.asJson<ImportResult>(res, 'Не удалось импортировать курсы');
    return {
      mode: data.mode,
      total: Number(data.total ?? 0),
      valid_count: Number(data.valid_count ?? 0),
      errors: Array.isArray(data.errors) ? data.errors : [],
      preview_token: data.preview_token ?? null,
      created_course_ids: Array.isArray(data.created_course_ids) ? data.created_course_ids : [],
    };
  }

  // ─── ENROLLMENTS (learner) ───────────────────────────────────────────────

  /**
   * POST /enrollments — идемпотентен по (user_id, course_id):
   * повторный вызов с тем же course_id возвращает существующую запись (не 409).
   * Фронт может смело дёргать на каждом «Открыть курс».
   */
  async enroll(courseId: number): Promise<EnrollmentOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/enrollments`, {
      method: 'POST',
      body: JSON.stringify({ course_id: courseId }),
    });
    return this.asJson<EnrollmentOut>(res, 'Не удалось записаться на курс');
  }

  async getEnrollment(enrollmentId: number): Promise<EnrollmentStatusResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/learning/enrollments/${enrollmentId}`);
    const data = await this.asJson<EnrollmentStatusResponse>(res, 'Запись на курс не найдена');
    return { ...data, progress: Array.isArray(data.progress) ? data.progress : [] };
  }

  // ─── MODULE DISPATCH (start/finish per module type) ──────────────────────
  // ВНИМАНИЕ: backend разделяет practice_task на отдельные start-practice/finish-practice.
  // Для video/text/quiz используем generic start-module/finish-module.

  async startModule(enrollmentId: number, moduleId: number): Promise<StartModuleResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/enrollments/${enrollmentId}/start-module/${moduleId}`,
      { method: 'POST' },
    );
    return this.asJson<StartModuleResponse>(res, 'Не удалось начать модуль');
  }

  async finishModule(enrollmentId: number, moduleId: number): Promise<FinishModuleResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/enrollments/${enrollmentId}/finish-module/${moduleId}`,
      { method: 'POST' },
    );
    return this.asJson<FinishModuleResponse>(res, 'Не удалось завершить модуль');
  }

  /**
   * Старт practice. Backend сам создаёт sandbox + возвращает готовый base64 header.
   * Caller обязан положить header в SandboxSessionContext.start() сразу после ответа,
   * иначе interceptor не будет инжектить его в последующие /tickets|/customers|... запросы.
   */
  async startPractice(enrollmentId: number, moduleId: number): Promise<StartPracticeResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/enrollments/${enrollmentId}/start-practice/${moduleId}`,
      { method: 'POST' },
    );
    return this.asJson<StartPracticeResponse>(res, 'Не удалось начать практику');
  }

  /**
   * Завершить practice. ВАЖНО: caller обязан вызвать sandbox.finish() ДО этого
   * запроса, чтобы header не попал в production-write (finish-practice пишет
   * в production learning_progress, а не в sandbox-схему).
   */
  async finishPractice(enrollmentId: number, moduleId: number): Promise<FinishPracticeResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/enrollments/${enrollmentId}/finish-practice/${moduleId}`,
      { method: 'POST' },
    );
    return this.asJson<FinishPracticeResponse>(res, 'Не удалось проверить практику');
  }

  /**
   * FINAL handoff §3.4 — Recovery flow.
   * GET /enrollments/{eid}/practice-header/{mid}
   *
   * Используется на mount AcademyAppLayout, когда страница обновилась посреди
   * practice. Фронт сам header не пересобирает (HMAC signed на бэке) — ходит
   * сюда за свежим подписанным.
   *
   * Caller обязан проверить status code:
   *   - 200 → setSession(header, expires_at) + показать banner
   *   - 404 → no active practice, ничего не делаем
   *   - 410 → expired, предложить «начать заново» через start-practice
   *   - 403 → чужой enrollment / нет permission
   */
  async getPracticeHeader(enrollmentId: number, moduleId: number): Promise<PracticeHeaderResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/enrollments/${enrollmentId}/practice-header/${moduleId}`,
    );
    return this.asJson<PracticeHeaderResponse>(res, 'Не удалось восстановить сессию практики');
  }

  // ─── PRESENTATION (PDF/PPTX) ──────────────────────────────────────────────

  /**
   * POST /courses/{courseId}/modules/upload-presentation — загрузить PDF/PPTX,
   * создать presentation-модуль. multipart/form-data: file, order, title.
   */
  async uploadPresentation(
    courseId: number,
    file: File,
    opts: { order: number; title: string },
  ): Promise<ModuleOut> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('order', String(opts.order));
    fd.append('title', opts.title);
    const res = await authService.fetchWithAuthForm(
      `${API_URL}/learning/courses/${courseId}/modules/upload-presentation`,
      { method: 'POST', body: fd },
    );
    return this.asJson<ModuleOut>(res, 'Не удалось загрузить презентацию');
  }

  /**
   * GET /courses/{courseId}/modules/{moduleId}/presentation-url —
   * получить временный URL для просмотра PDF.
   */
  async getPresentationUrl(courseId: number, moduleId: number): Promise<PresentationUrlResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/courses/${courseId}/modules/${moduleId}/presentation-url`,
    );
    return this.asJson<PresentationUrlResponse>(res, 'Не удалось получить ссылку на презентацию');
  }

  /**
   * POST /enrollments/{enrollmentId}/presentation-page/{moduleId} —
   * сохранить прогресс по слайдам. Возвращает {last_page}.
   */
  async savePresentationPage(
    enrollmentId: number,
    moduleId: number,
    page: number,
  ): Promise<PresentationPageResponse> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/enrollments/${enrollmentId}/presentation-page/${moduleId}`,
      {
        method: 'POST',
        body: JSON.stringify({ page }),
      },
    );
    return this.asJson<PresentationPageResponse>(res, 'Не удалось сохранить прогресс презентации');
  }

  /**
   * POST /courses/{courseId}/modules/import-presentation/dry-run —
   * dry-run AI-импорта PPTX. multipart, поле file.
   * Возвращает PresentationImportResult с preview + preview_token.
   */
  async importPresentationDryRun(
    courseId: number,
    file: File,
    signal?: AbortSignal,
  ): Promise<PresentationImportResult> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await authService.fetchWithAuthForm(
      `${API_URL}/learning/courses/${courseId}/modules/import-presentation/dry-run`,
      { method: 'POST', body: fd, signal },
    );
    return this.asJson<PresentationImportResult>(res, 'Не удалось выполнить анализ презентации');
  }

  /**
   * POST /courses/{courseId}/modules/import-presentation/commit —
   * commit AI-импорта с preview_token TOCTOU-защитой. application/json.
   * Возвращает PresentationImportResult с created_module_ids.
   */
  async importPresentationCommit(
    courseId: number,
    body: PresentationImportCommitRequest,
    signal?: AbortSignal,
  ): Promise<PresentationImportResult> {
    const res = await authService.fetchWithAuth(
      `${API_URL}/learning/courses/${courseId}/modules/import-presentation/commit`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      },
    );
    return this.asJson<PresentationImportResult>(res, 'Не удалось импортировать презентацию');
  }

  /**
   * POST /courses/{courseId}/modules/{moduleId}/reversion-presentation —
   * заменить файл презентации, бамп version, пометить прогрессы stale.
   * multipart, поле file. Возвращает {version, stale_count}.
   */
  async reversionPresentation(
    courseId: number,
    moduleId: number,
    file: File,
  ): Promise<ReversionResult> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await authService.fetchWithAuthForm(
      `${API_URL}/learning/courses/${courseId}/modules/${moduleId}/reversion-presentation`,
      { method: 'POST', body: fd },
    );
    return this.asJson<ReversionResult>(res, 'Не удалось обновить версию презентации');
  }
}

const learningService = new LearningService();
export default learningService;
export { learningService };
